import type { PromotionInterface } from '@/models/shop/Promotion.types'
import type { ArenaProductVariant } from '@/models/shop/ArenaProduct.types'
import { getVariantPrice } from '@/utils/variant'
import { COIN_TYPES } from '@/constants/coins'
import { CurrencyInterface } from 'src/models/shop/Currency'

type NullableId = string | number | null | undefined

type CategoryLike = {
  id?: NullableId
  code?: string
}

type VariantLike = {
  id?: NullableId
  arena_variant_id?: NullableId
  variant_id?: NullableId
  category?: { code?: string }
  category_code?: string
  arena_category?: { code?: string }
  arena_product?: {
    id?: NullableId
    category_code?: string
    category?: { code?: string }
  }
  arena_product_id?: NullableId
}

type PromotionGroupLike = {
  id?: string
  tempId?: string
  name?: string
  quantity?: number | string
  variants?: VariantLike[]
  variantIds?: Array<string | number | VariantLike>
  category_id?: NullableId
  categoryId?: NullableId
  category?: { id?: NullableId; code?: string }
  category_code?: string
  categoryCode?: string
}

type PromotionLike = {
  id?: NullableId
  tempId?: string | null
  arena_id?: NullableId
  name?: string
  image?: string
  description?: string
  discount_value?: string | number | null
  discount_type?: string
  sort_order?: number
  active?: boolean
  standard_price?: number | string
  promo_price?: number | string
  groups?: PromotionGroupLike[]
}

export const createPriceSummaryState = () => ({
  standardPrice: 0,
  discountValue: 0,
  promoPrice: 0,
})

export const sanitizeDiscountInput = (value: unknown) => {
  if (value === null || value === undefined) return ''
  let stringValue = String(value)
  stringValue = stringValue.replace(',', '.')
  stringValue = stringValue.replace(/[^\d.]/g, '')
  if (!stringValue) return ''
  const firstDotIndex = stringValue.indexOf('.')
  if (firstDotIndex === -1) {
    const normalizedInteger = stringValue.replace(/^0+(?=\d)/, '')
    return normalizedInteger || '0'
  }
  let integerPart = stringValue.slice(0, firstDotIndex)
  let fractionalPart = stringValue.slice(firstDotIndex + 1)
  fractionalPart = fractionalPart.replace(/\./g, '')
  integerPart = integerPart.replace(/^0+(?=\d)/, '')
  if (!integerPart) {
    integerPart = '0'
  }
  return `${integerPart}.${fractionalPart}`
}

export const formatDiscountInputValue = (value: unknown) => {
  const sanitized = sanitizeDiscountInput(value)
  if (!sanitized) return ''
  let workingValue = sanitized
  if (!workingValue.includes('.')) {
    workingValue = `${workingValue}.`
  }
  let [integerPart = '0', fractionalPart = ''] = workingValue.split('.')
  integerPart = integerPart.replace(/^0+(?=\d)/, '') || '0'
  const requiredFractionDigits = integerPart.length <= 1 ? 2 : 1
  if (fractionalPart.length < requiredFractionDigits) {
    fractionalPart = fractionalPart.padEnd(requiredFractionDigits, '0')
  }
  return `${integerPart}.${fractionalPart}`
}

export const normalizeDiscountAmount = (value: unknown) => {
  const parsed = Number(value)
  if (Number.isNaN(parsed) || parsed < 0) return 0
  return parsed
}

export const roundCurrencyValue = (value: unknown) => {
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return 0
  return Math.round(numeric * 100) / 100
}

export const normalizeVariantId = (value: unknown) => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') {
    const variant = value as VariantLike
    const resolved =
      variant?.arena_variant_id || variant?.variant_id || variant?.id || ''
    return resolved ? String(resolved) : ''
  }
  return String(value)
}

export const normalizeVariantIdList = (values: unknown[] = []) => {
  if (!Array.isArray(values)) return []
  const unique: string[] = []
  const seen = new Set<string>()
  values.forEach((value) => {
    const normalized = normalizeVariantId(value)
    if (!normalized || seen.has(normalized)) return
    seen.add(normalized)
    unique.push(normalized)
  })
  return unique
}

export const extractVariantIdsFromGroup = (group: PromotionGroupLike = {}) => {
  if (!group) return []
  const combined: unknown[] = []
  if (Array.isArray(group.variantIds)) {
    combined.push(...group.variantIds)
  }
  if (Array.isArray(group.variants)) {
    combined.push(...group.variants)
  }
  return normalizeVariantIdList(combined)
}

export const extractCategoryCodeFromVariants = (
  variants: VariantLike[] | undefined,
) => {
  if (!Array.isArray(variants)) return ''
  for (const variant of variants) {
    const code =
      variant?.category?.code ||
      variant?.category_code ||
      variant?.arena_category?.code ||
      variant?.arena_product?.category_code ||
      variant?.arena_product?.category?.code ||
      ''
    if (code) return code
  }
  return ''
}

export const getCategoryIdByCode = (
  categories: CategoryLike[],
  code: string,
) => {
  if (!code || !Array.isArray(categories)) return ''
  const match = categories.find((category) => category.code === code)
  return (match?.id || match?.id === 0) && match.id !== null
    ? String(match.id)
    : ''
}

export const getCategoryCodeById = (
  categories: CategoryLike[],
  categoryId: NullableId,
) => {
  if (
    categoryId === null ||
    categoryId === undefined ||
    !Array.isArray(categories)
  ) {
    return ''
  }
  const normalizedId = String(categoryId)
  const match = categories.find(
    (category) => String(category.id) === normalizedId,
  )
  return match?.code || ''
}

export const deriveGroupCategoryId = (
  group: PromotionGroupLike,
  categories: CategoryLike[],
) => {
  if (!group) return ''
  const directId =
    group.category_id || group.categoryId || group.category?.id || ''
  if (directId || directId === 0) {
    return String(directId).trim()
  }
  const code =
    group.category_code ||
    group.categoryCode ||
    group.category?.code ||
    group.category ||
    ''
  if (code) {
    return getCategoryIdByCode(categories, String(code)) || ''
  }
  return ''
}

export const deriveGroupCategoryCode = (
  group: PromotionGroupLike,
  categories: CategoryLike[],
) => {
  if (!group) return ''
  const directCategory =
    group.category_code ||
    group.categoryCode ||
    group.category?.code ||
    group.category ||
    ''
  if (directCategory) return directCategory
  const categoryId =
    group.category_id || group.categoryId || group.category?.id || ''
  if (categoryId || categoryId === 0) {
    const resolvedCode = getCategoryCodeById(categories, categoryId)
    if (resolvedCode) return resolvedCode
  }
  return extractCategoryCodeFromVariants(group.variants)
}

export const buildLocalGroup = (
  group: PromotionGroupLike,
  categories: CategoryLike[],
) => {
  const derivedVariantIds = extractVariantIdsFromGroup(group)
  const variantIds = group?.id ? [] : derivedVariantIds
  const id =
    group?.id ||
    group?.tempId ||
    `temp-group-${Date.now()}-${Math.random().toString(16).slice(2)}`
  const variants = Array.isArray(group?.variants)
    ? group.variants.map((variant: VariantLike) => ({ ...variant }))
    : []
  const tempId = group?.tempId || (group?.id ? '' : id)
  return {
    id,
    tempId,
    name: group?.name || '',
    quantity: group?.quantity || 0,
    variants,
    variantIds,
    category_id: deriveGroupCategoryId(group, categories),
    category_code: deriveGroupCategoryCode(group, categories),
  }
}

export const createLocalPromotionState = (
  promotion: PromotionLike,
  categories: CategoryLike[],
) => {
  const source = promotion ? JSON.parse(JSON.stringify(promotion)) : {}
  const defaults = {
    id: null,
    tempId: source.tempId || null,
    arena_id: source.arena_id || null,
    name: '',
    image: '',
    description: '',
    discount_value:
      source.discount_value !== undefined && source.discount_value !== null
        ? formatDiscountInputValue(source.discount_value)
        : '',
    discount_type:
      source.discount_type !== undefined && source.discount_type !== null
        ? source.discount_type
        : 'percentage',
    sort_order: 0,
    active: false,
    standard_price: '',
    promo_price: '',
    groups: [],
  }

  return {
    ...defaults,
    ...source,
    groups: (source.groups || []).map((group: PromotionGroupLike) =>
      buildLocalGroup(group, categories),
    ),
  }
}

const getGroupVariants = (
  group: PromotionGroupLike,
  availableVariants: ArenaProductVariant[],
) => {
  const variantIds = extractVariantIdsFromGroup(group)
  if (!variantIds.length) return []

  return availableVariants.filter((variant) =>
    variantIds.includes(normalizeVariantId(variant.id)),
  )
}

const getMostExpensiveVariantInGroup = (
  group: PromotionGroupLike,
  availableVariants: ArenaProductVariant[],
) => {
  const groupVariants = getGroupVariants(group, availableVariants)
  if (!groupVariants.length) return

  return groupVariants.reduce<ArenaProductVariant>((mostExpensive, variant) => {
    const currentPrice = Number(mostExpensive?.prices?.[0]?.gross_price || 0)
    const variantPrice = Number(variant?.prices?.[0]?.gross_price || 0)
    return variantPrice > currentPrice ? variant : mostExpensive
  }, groupVariants[0])
}

export const isFixedPricePromotion = (
  promotion: PromotionInterface | null | undefined,
): boolean => {
  if (!promotion) {
    return false
  }
  return promotion.discount_type === 'fixed_price'
}

export const calculatePromotionPrice = (
  promotion: PromotionInterface,
  availableVariants: ArenaProductVariant[],
  arenaCurrency?: CurrencyInterface,
) => {
  if (!promotion?.groups?.length) return

  if (!arenaCurrency) return

  const coinsPerCurrency = arenaCurrency.coins_per_currency
  return promotion.groups.reduce((sum, group) => {
    const mostExpensiveInGroup = getMostExpensiveVariantInGroup(
      group,
      availableVariants,
    )
    if (!mostExpensiveInGroup) return sum

    const isCoinVariant =
      mostExpensiveInGroup?.attributes?.type === COIN_TYPES.coins
    const groupQuantity = group?.quantity || 0
    const variantPrice = isCoinVariant
      ? groupQuantity / coinsPerCurrency
      : groupQuantity * Number(getVariantPrice(mostExpensiveInGroup))

    if (!variantPrice) return sum

    return sum + variantPrice
  }, 0)
}
